import { useCallback, useRef } from 'react';

const disableAtWidth = 720;

interface ContactRefSet {
  refParent: HTMLAnchorElement | null;
  ref: HTMLSpanElement | null;
  width: number;
}

interface DataSet {
  gen: { ref: HTMLDivElement | null };
  email: ContactRefSet;
  phone: ContactRefSet;
}

/**
 * Encapsulates the imperative DOM measurement and hover expand/collapse
 * logic for the FundsManager contact circle animation.
 *
 * - Measures email/phone text widths once refs are mounted
 * - On hover: scales background circle and translates contact links to reveal text
 * - On leave: reverses the animation
 *
 * Returns ref setters and mouse handlers to wire into JSX.
 */
export function useFundsManagerHover() {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxWidthRef = useRef(0);

  const dataSet = useRef<DataSet>({
    gen: { ref: null },
    email: { refParent: null, ref: null, width: 0 },
    phone: { refParent: null, ref: null, width: 0 },
  });

  // --- ref setters (stable callbacks) ---

  const setWrapperRef = useCallback((dom: HTMLDivElement | null) => {
    wrapperRef.current = dom;
  }, []);

  const setBackgroundRef = useCallback((dom: HTMLDivElement | null) => {
    dataSet.current.gen.ref = dom;
  }, []);

  const setEmailParentRef = useCallback((dom: HTMLAnchorElement | null) => {
    dataSet.current.email.refParent = dom;
  }, []);

  const setEmailTextRef = useCallback((dom: HTMLSpanElement | null) => {
    dataSet.current.email.ref = dom;
  }, []);

  const setPhoneParentRef = useCallback((dom: HTMLAnchorElement | null) => {
    dataSet.current.phone.refParent = dom;
  }, []);

  const setPhoneTextRef = useCallback((dom: HTMLSpanElement | null) => {
    dataSet.current.phone.ref = dom;
  }, []);

  // --- width calculation ---

  const calcWidth = useCallback(() => {
    let needsRecalc = false;
    const ds = dataSet.current;

    for (const key of ['email', 'phone'] as const) {
      const entry = ds[key];
      if (!entry.ref || !entry.refParent) {
        needsRecalc = true;
        continue;
      }
      entry.refParent.classList.remove('shrink');
      entry.width = entry.ref.offsetWidth;
      if (entry.width > maxWidthRef.current) {
        maxWidthRef.current = entry.width;
      }
      entry.refParent.classList.add('shrink');
    }

    if (needsRecalc) {
      setTimeout(calcWidth, 100);
    }
  }, []);

  // kick off initial measurement
  // (caller should invoke this once via setTimeout like the original)
  const startMeasurement = useCallback(() => {
    setTimeout(calcWidth, 1);
  }, [calcWidth]);

  // --- show / hide background ---

  const showBackground = useCallback((refBackground: HTMLDivElement | null) => {
    if (!refBackground) {
      return;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    refBackground.style.transform = `scale( ${Math.max(maxWidthRef.current / 190, 1)} )`;
    refBackground.classList.add('is-visible');
    wrapperRef.current?.classList.add('is-extended');
  }, []);

  const hideBackground = useCallback((refBackground: HTMLDivElement | null) => {
    if (!refBackground) {
      return;
    }
    wrapperRef.current?.classList.remove('is-extended');
    hideTimeoutRef.current = setTimeout(() => {
      refBackground.style.transform = 'scale( 0 )';
      refBackground.classList.remove('is-visible');
    }, 50);
  }, []);

  // --- mouse handlers ---

  const onMouseEnter = useCallback(() => {
    const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

    if (windowWidth < disableAtWidth) {
      return true;
    }

    const ds = dataSet.current;

    showBackground(ds.gen.ref);

    for (const key of ['email', 'phone'] as const) {
      const entry = ds[key];
      if (!entry.refParent || !entry.width) {
        continue;
      }
      entry.refParent.style.transform = `translateX( -${entry.width}px )`;
    }
  }, [showBackground]);

  const onMouseLeave = useCallback(() => {
    const ds = dataSet.current;

    hideBackground(ds.gen.ref);

    for (const key of ['email', 'phone'] as const) {
      const entry = ds[key];
      if (!entry.refParent) {
        continue;
      }
      entry.refParent.style.transform = 'translateX( 0 )';
    }
  }, [hideBackground]);

  return {
    // ref setters
    setWrapperRef,
    setBackgroundRef,
    setEmailParentRef,
    setEmailTextRef,
    setPhoneParentRef,
    setPhoneTextRef,
    // lifecycle
    startMeasurement,
    // handlers
    onMouseEnter,
    onMouseLeave,
  };
}
